import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Alert,
  AlertTitle,
  CircularProgress,
  Tooltip,
  alpha,
  useTheme,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Webhook,
  ContentCopy,
  Refresh,
  Visibility,
  CheckCircle,
  Error,
  Warning,
  Schedule,
  FilterList,
  Search,
  Add,
  Delete,
  Link,
  LinkOff,
  Pix,
  Receipt,
  Send,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { Collapse } from '@mui/material';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface WebhookConfig {
  webhookId: string;
  webhookUrl: string;
  companyName: string;
  stats: {
    total: number;
    processed: number;
    failed: number;
    lastWebhookAt: string | null;
  };
}

interface WebhookLog {
  id: string;
  webhookType: string;
  eventType: string;
  txid: string | null;
  endToEndId: string | null;
  status: string;
  processingError: string | null;
  ipAddress: string;
  createdAt: string;
  processedAt: string | null;
}

interface WebhookLogDetail extends WebhookLog {
  method: string;
  path: string;
  headers: Record<string, string>;
  queryParams: Record<string, string>;
  body: any;
  rawBody: string;
  userAgent: string;
}

interface SicoobWebhookStatus {
  ourWebhookUrl: string;
  pixRecebimentos: {
    configured: boolean;
    webhookUrl: string | null;
    chave?: string;
    criacao?: string;
    error: string | null;
  };
  pixPagamentos: {
    configured: boolean;
    webhookUrl: string | null;
    criacao?: string;
    error: string | null;
  };
  boletos: {
    configured: boolean;
    webhooks: Array<{
      id: number;
      url: string;
      tipoMovimento: string;
      situacao: string;
      dataCadastro: string;
    }>;
    error: string | null;
  };
}

const WebhookLogsPage: React.FC = () => {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<WebhookConfig | null>(null);
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  const [selectedLog, setSelectedLog] = useState<WebhookLogDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Sicoob webhook registration
  const [sicoobStatus, setSicoobStatus] = useState<SicoobWebhookStatus | null>(null);
  const [loadingSicoob, setLoadingSicoob] = useState(false);
  const [showSicoobConfig, setShowSicoobConfig] = useState(false);
  const [pixChave, setPixChave] = useState('');
  const [registeringWebhook, setRegisteringWebhook] = useState<string | null>(null);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTxid, setFilterTxid] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage, filterType, filterStatus, filterTxid]);

  const loadConfig = async () => {
    try {
      const response = await api.get('/webhook-config/config');
      if (response?.success) {
        setConfig(response.data);
      }
    } catch (error: any) {
      console.error('Error loading webhook config:', error);
      toast.error('Erro ao carregar configurações de webhook');
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });

      if (filterType) params.append('webhookType', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (filterTxid) params.append('txid', filterTxid);

      const response = await api.get(`/webhook-config/logs?${params}`);
      if (response?.success) {
        setLogs(response.data);
        setTotalLogs(response.meta?.total || 0);
      }
    } catch (error: any) {
      console.error('Error loading webhook logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = () => {
    if (config?.webhookUrl) {
      navigator.clipboard.writeText(config.webhookUrl);
      toast.success('URL copiada para a área de transferência');
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await api.post('/webhook-config/config/regenerate');
      if (response?.success) {
        setConfig(prev => prev ? {
          ...prev,
          webhookId: response.data.webhookId,
          webhookUrl: response.data.webhookUrl
        } : null);
        toast.success('Webhook URL regenerada com sucesso');
        setConfirmRegenerate(false);
      }
    } catch (error: any) {
      toast.error('Erro ao regenerar webhook URL');
    } finally {
      setRegenerating(false);
    }
  };

  const loadSicoobStatus = async () => {
    setLoadingSicoob(true);
    try {
      const response = await api.get('/webhook-config/sicoob/status');
      if (response?.success) {
        setSicoobStatus(response.data);
      }
    } catch (error: any) {
      console.error('Error loading Sicoob status:', error);
    } finally {
      setLoadingSicoob(false);
    }
  };

  const handleRegisterPixRecebimentos = async () => {
    if (!pixChave.trim()) {
      toast.error('Informe a chave PIX');
      return;
    }
    setRegisteringWebhook('pix-recebimentos');
    try {
      const response = await api.post('/webhook-config/sicoob/pix-recebimentos', { chave: pixChave });
      if (response?.success) {
        toast.success('Webhook PIX Recebimentos registrado!');
        setPixChave('');
        loadSicoobStatus();
      } else {
        toast.error(response?.error?.message || 'Erro ao registrar webhook');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao registrar webhook');
    } finally {
      setRegisteringWebhook(null);
    }
  };

  const handleDeletePixRecebimentos = async (chave: string) => {
    if (!window.confirm('Remover webhook de PIX Recebimentos?')) return;
    setRegisteringWebhook('pix-recebimentos-delete');
    try {
      const response = await api.delete('/webhook-config/sicoob/pix-recebimentos', { data: { chave } });
      if (response?.success) {
        toast.success('Webhook removido');
        loadSicoobStatus();
      }
    } catch (error: any) {
      toast.error('Erro ao remover webhook');
    } finally {
      setRegisteringWebhook(null);
    }
  };

  const handleRegisterPixPagamentos = async () => {
    setRegisteringWebhook('pix-pagamentos');
    try {
      const response = await api.post('/webhook-config/sicoob/pix-pagamentos');
      if (response?.success) {
        toast.success('Webhook PIX Pagamentos registrado!');
        loadSicoobStatus();
      } else {
        toast.error(response?.error?.message || 'Erro ao registrar webhook');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao registrar webhook');
    } finally {
      setRegisteringWebhook(null);
    }
  };

  const handleDeletePixPagamentos = async () => {
    if (!window.confirm('Remover webhook de PIX Pagamentos?')) return;
    setRegisteringWebhook('pix-pagamentos-delete');
    try {
      const response = await api.delete('/webhook-config/sicoob/pix-pagamentos');
      if (response?.success) {
        toast.success('Webhook removido');
        loadSicoobStatus();
      }
    } catch (error: any) {
      toast.error('Erro ao remover webhook');
    } finally {
      setRegisteringWebhook(null);
    }
  };

  const handleRegisterBoletos = async () => {
    setRegisteringWebhook('boletos');
    try {
      const response = await api.post('/webhook-config/sicoob/boletos', { codigoTipoMovimento: 7 });
      if (response?.success) {
        toast.success('Webhook de Boletos registrado!');
        loadSicoobStatus();
      } else {
        toast.error(response?.error?.message || 'Erro ao registrar webhook');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao registrar webhook');
    } finally {
      setRegisteringWebhook(null);
    }
  };

  const handleDeleteBoleto = async (idWebhook: number) => {
    if (!window.confirm('Remover webhook de Boletos?')) return;
    setRegisteringWebhook(`boleto-${idWebhook}`);
    try {
      const response = await api.delete(`/webhook-config/sicoob/boletos/${idWebhook}`);
      if (response?.success) {
        toast.success('Webhook removido');
        loadSicoobStatus();
      }
    } catch (error: any) {
      toast.error('Erro ao remover webhook');
    } finally {
      setRegisteringWebhook(null);
    }
  };

  const handleViewDetail = async (logId: string) => {
    setLoadingDetail(true);
    try {
      const response = await api.get(`/webhook-config/logs/${logId}`);
      if (response?.success) {
        setSelectedLog(response.data);
      }
    } catch (error: any) {
      toast.error('Erro ao carregar detalhes');
    } finally {
      setLoadingDetail(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'processed':
        return <Chip icon={<CheckCircle sx={{ fontSize: 16 }} />} label="Processado" size="small" color="success" />;
      case 'failed':
        return <Chip icon={<Error sx={{ fontSize: 16 }} />} label="Falha" size="small" color="error" />;
      case 'processing':
        return <Chip icon={<Schedule sx={{ fontSize: 16 }} />} label="Processando" size="small" color="warning" />;
      case 'received':
        return <Chip icon={<Schedule sx={{ fontSize: 16 }} />} label="Recebido" size="small" color="info" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'pix':
        return 'primary';
      case 'boleto':
        return 'warning';
      case 'pix_cob':
      case 'pix_cobv':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            <Webhook sx={{ mr: 1, verticalAlign: 'middle' }} />
            Webhooks Sicoob
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure e monitore os webhooks recebidos do Sicoob
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => { loadConfig(); loadLogs(); }}
        >
          Atualizar
        </Button>
      </Box>

      {/* Configuração */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Configuração do Webhook
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>URL para configurar no Sicoob</AlertTitle>
          Use esta URL nas configurações de webhook do Sicoob para receber notificações de PIX, boletos e outras operações.
        </Alert>

        <TextField
          fullWidth
          label="Webhook URL"
          value={config?.webhookUrl || 'Carregando...'}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Copiar URL">
                  <IconButton onClick={handleCopyUrl}>
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Webhook ID: <code>{config?.webhookId || '...'}</code>
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={() => setConfirmRegenerate(true)}
          >
            Gerar Nova URL
          </Button>
        </Box>
      </Paper>

      {/* Registro de Webhooks no Sicoob */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => {
            setShowSicoobConfig(!showSicoobConfig);
            if (!sicoobStatus && !loadingSicoob) loadSicoobStatus();
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              <Link sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
              Registrar Webhooks no Sicoob
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure automaticamente sua URL nas APIs do Sicoob
            </Typography>
          </Box>
          <IconButton>
            {showSicoobConfig ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={showSicoobConfig}>
          <Box sx={{ mt: 3 }}>
            {loadingSicoob ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Consultando webhooks no Sicoob...
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {/* PIX Recebimentos */}
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Pix sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          PIX Recebimentos
                        </Typography>
                      </Box>

                      {sicoobStatus?.pixRecebimentos.configured ? (
                        <>
                          <Chip
                            icon={<CheckCircle />}
                            label="Configurado"
                            color="success"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Chave: {sicoobStatus.pixRecebimentos.chave}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => handleDeletePixRecebimentos(sicoobStatus.pixRecebimentos.chave!)}
                            disabled={registeringWebhook === 'pix-recebimentos-delete'}
                          >
                            Remover
                          </Button>
                        </>
                      ) : (
                        <>
                          <Chip
                            icon={<LinkOff />}
                            label="Não configurado"
                            color="default"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Chave PIX"
                            placeholder="CPF, CNPJ, email, telefone ou EVP"
                            value={pixChave}
                            onChange={(e) => setPixChave(e.target.value)}
                            sx={{ mb: 1 }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={registeringWebhook === 'pix-recebimentos' ? <CircularProgress size={16} /> : <Add />}
                            onClick={handleRegisterPixRecebimentos}
                            disabled={registeringWebhook === 'pix-recebimentos'}
                          >
                            Registrar
                          </Button>
                        </>
                      )}
                      {sicoobStatus?.pixRecebimentos.error && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                          <Typography variant="caption">{sicoobStatus.pixRecebimentos.error}</Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* PIX Pagamentos */}
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Send sx={{ mr: 1, color: 'info.main' }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          PIX Pagamentos
                        </Typography>
                      </Box>

                      {sicoobStatus?.pixPagamentos.configured ? (
                        <>
                          <Chip
                            icon={<CheckCircle />}
                            label="Configurado"
                            color="success"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Notificações de PIX enviados ativas
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={handleDeletePixPagamentos}
                            disabled={registeringWebhook === 'pix-pagamentos-delete'}
                          >
                            Remover
                          </Button>
                        </>
                      ) : (
                        <>
                          <Chip
                            icon={<LinkOff />}
                            label="Não configurado"
                            color="default"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Receba notificações de PIX enviados
                          </Typography>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={registeringWebhook === 'pix-pagamentos' ? <CircularProgress size={16} /> : <Add />}
                            onClick={handleRegisterPixPagamentos}
                            disabled={registeringWebhook === 'pix-pagamentos'}
                          >
                            Registrar
                          </Button>
                        </>
                      )}
                      {sicoobStatus?.pixPagamentos.error && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                          <Typography variant="caption">{sicoobStatus.pixPagamentos.error}</Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Boletos */}
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Receipt sx={{ mr: 1, color: 'warning.main' }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Boletos
                        </Typography>
                      </Box>

                      {sicoobStatus?.boletos.configured && sicoobStatus.boletos.webhooks.length > 0 ? (
                        <>
                          <Chip
                            icon={<CheckCircle />}
                            label={`${sicoobStatus.boletos.webhooks.length} webhook(s)`}
                            color="success"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          {sicoobStatus.boletos.webhooks.map((w) => (
                            <Box key={w.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {w.tipoMovimento}
                              </Typography>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteBoleto(w.id)}
                                disabled={registeringWebhook === `boleto-${w.id}`}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </>
                      ) : (
                        <>
                          <Chip
                            icon={<LinkOff />}
                            label="Não configurado"
                            color="default"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Receba notificações de pagamentos de boletos
                          </Typography>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={registeringWebhook === 'boletos' ? <CircularProgress size={16} /> : <Add />}
                            onClick={handleRegisterBoletos}
                            disabled={registeringWebhook === 'boletos'}
                          >
                            Registrar
                          </Button>
                        </>
                      )}
                      {sicoobStatus?.boletos.error && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                          <Typography variant="caption">{sicoobStatus.boletos.error}</Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Ao registrar, sua URL de webhook será configurada automaticamente nas APIs do Sicoob.
                As notificações serão recebidas nesta mesma URL.
              </Typography>
            </Alert>
          </Box>
        </Collapse>
      </Paper>

      {/* Estatísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="primary">
                {config?.stats.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Recebidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {config?.stats.processed || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Processados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="error.main">
                {config?.stats.failed || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Com Falha
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {config?.stats.lastWebhookAt
                  ? formatDate(config.stats.lastWebhookAt)
                  : 'Nenhum'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Último Recebido
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filterType}
                label="Tipo"
                onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pix">PIX</MenuItem>
                <MenuItem value="pix_cob">PIX Cobrança</MenuItem>
                <MenuItem value="pix_cobv">PIX Cobrança c/ Vencimento</MenuItem>
                <MenuItem value="boleto">Boleto</MenuItem>
                <MenuItem value="pix_devolucao">PIX Devolução</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="processed">Processado</MenuItem>
                <MenuItem value="failed">Falha</MenuItem>
                <MenuItem value="processing">Processando</MenuItem>
                <MenuItem value="received">Recebido</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por txid ou endToEndId"
              value={filterTxid}
              onChange={(e) => setFilterTxid(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => { if (e.key === 'Enter') { setPage(0); loadLogs(); }}}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => { setPage(0); loadLogs(); }}
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de Logs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Data/Hora</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Evento</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>txid / endToEndId</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>IP</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Webhook sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    Nenhum webhook recebido ainda
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(log.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.webhookType}
                      size="small"
                      color={getTypeColor(log.webhookType) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{log.eventType}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {log.txid || log.endToEndId || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(log.status)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {log.ipAddress}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver Detalhes">
                      <IconButton size="small" onClick={() => handleViewDetail(log.id)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalLogs}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>

      {/* Dialog Confirmar Regenerar */}
      <Dialog open={confirmRegenerate} onClose={() => setConfirmRegenerate(false)}>
        <DialogTitle>Gerar Nova URL de Webhook?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <AlertTitle>Atenção</AlertTitle>
            Ao gerar uma nova URL, a URL anterior será invalidada. Você precisará atualizar a configuração no Sicoob.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRegenerate(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRegenerate}
            disabled={regenerating}
          >
            {regenerating ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Detalhes do Webhook */}
      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalhes do Webhook
          {selectedLog && (
            <Chip
              label={selectedLog.webhookType}
              size="small"
              color={getTypeColor(selectedLog.webhookType) as any}
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedLog && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>{getStatusChip(selectedLog.status)}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Recebido em</Typography>
                  <Typography variant="body2">{formatDate(selectedLog.createdAt)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">IP de Origem</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedLog.ipAddress}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Método / Path</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedLog.method} {selectedLog.path}
                  </Typography>
                </Grid>
              </Grid>

              {selectedLog.txid && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">txid</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedLog.txid}</Typography>
                </Box>
              )}

              {selectedLog.endToEndId && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">endToEndId</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedLog.endToEndId}</Typography>
                </Box>
              )}

              {selectedLog.processingError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <AlertTitle>Erro de Processamento</AlertTitle>
                  {selectedLog.processingError}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Payload Recebido (Body)</Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.common.black, 0.02),
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedLog.body, null, 2)}
                </pre>
              </Paper>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Headers</Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.common.black, 0.02),
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedLog.headers, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebhookLogsPage;
